/**
 * MIRA by MARFI — floating AI feedback widget.
 *
 * One component, one mount point in <Layout>. Owns:
 *  - The floating pill (bottom-right) with the red MARFI logo
 *  - The chat drawer (slate-900 header, vertical category chips)
 *  - Multi-turn chat against /api/feedback/chat (Gemini 3 Flash)
 *  - Optional screenshot via html2canvas (lazy-loaded one-shot)
 *  - Ticket submission via /api/feedback/submit
 *  - "✺ My tickets" drawer header pill (toggles into MyTicketsPanel)
 *  - "Shipped recently" footer
 *
 * Severity is INFERRED by the model. The user never picks it.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  X,
  Send,
  Camera,
  Bug,
  Sparkles,
  HelpCircle,
  MoreHorizontal,
  ExternalLink,
  Loader2,
  Image as ImageIcon,
  ChevronLeft,
  EyeOff,
  SquarePen,
} from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { miraChat, miraCaptureScreen, miraSubmit, miraShipped } from "@/lib/api";
import { collectTelemetry } from "./telemetry";
import MyTicketsPanel from "./MyTicketsPanel";

const CATEGORY_CHIPS = [
  {
    id: "bug",
    label: "Something's broken",
    Icon: Bug,
    color: "text-rose-700",
    border: "border-rose-200",
    bg: "bg-rose-50 hover:bg-rose-100",
    seed: "Something's broken on this page. ",
  },
  {
    id: "feature",
    label: "Feature Idea",
    Icon: Sparkles,
    color: "text-emerald-700",
    border: "border-emerald-200",
    bg: "bg-emerald-50 hover:bg-emerald-100",
    seed: "I have a feature idea: ",
  },
  {
    id: "question",
    label: "Question",
    Icon: HelpCircle,
    color: "text-sky-700",
    border: "border-sky-200",
    bg: "bg-sky-50 hover:bg-sky-100",
    seed: "I have a question — ",
  },
  {
    id: "other",
    label: "Something Else",
    Icon: MoreHorizontal,
    color: "text-slate-700",
    border: "border-slate-200",
    bg: "bg-slate-50 hover:bg-slate-100",
    seed: "",
  },
];

export default function MiraWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  useEffect(() => { window.dispatchEvent(new CustomEvent(open ? "mira:opened" : "mira:closed")); }, [open]);
  // MIRA ticket #267 — power users can hide the pill. Persisted in
  // localStorage so it survives reloads. A 'mira:show' window event
  // (fired from /profile → "Show Mira" button) flips it back on
  // without requiring a route change.
  const [hidden, setHidden] = useState(() => {
    if (typeof window === "undefined") return false;
    // iter401 — Mira is OFF by default; only Profile → "Show Mira" turns the pill on.
    try { return localStorage.getItem("mira_hidden") !== "0"; } catch { return true; }
  });
  useEffect(() => {
    const onShow = () => setHidden(false);
    // #271 — profile page's visibility switch can also HIDE Mira from
    // the same tab; mirror it here (storage events only fire cross-tab).
    const onHide = () => { setHidden(true); setOpen(false); };
    const onSync = (e) => {
      // cross-tab sync — if another tab hides/shows MIRA we mirror it
      if (e.key === "mira_hidden") {
        setHidden(e.newValue !== "0");
      }
    };
    window.addEventListener("mira:show", onShow);
    const onOpen = () => { setHidden(false); setOpen(true); };
    window.addEventListener("mira:open", onOpen);
    const onToggle = () => { setHidden(false); setOpen((o) => !o); };
    window.addEventListener("mira:toggle", onToggle);
    window.addEventListener("mira:hide", onHide);
    window.addEventListener("storage", onSync);
    return () => {
      window.removeEventListener("mira:show", onShow);
      window.removeEventListener("mira:open", onOpen);
      window.removeEventListener("mira:toggle", onToggle);
      window.removeEventListener("mira:hide", onHide);
      window.removeEventListener("storage", onSync);
    };
  }, []);
  const hideWidget = () => {
    try { localStorage.setItem("mira_hidden", "1"); } catch { /* private mode */ }
    setHidden(true);
    setOpen(false);
    // #271 — same-tab signal so the /profile visibility switch flips
    // immediately if that page is currently mounted.
    window.dispatchEvent(new Event("mira:hide"));
    toast("Mira hidden", {
      description: "Turn it back on from your Profile page.",
      duration: 5000,
      // #272 — sonner's default description uses `text-muted-foreground`
      // which is near-white on the white toast background in some
      // themes. Force dark slate so it's always readable.
      classNames: { description: "!text-slate-700 !opacity-100" },
    });
  };
  const [view, setView] = useState("chat"); // "chat" | "tickets"
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]); // [{ role: "user"|"mira", text }]
  const [quickReplies, setQuickReplies] = useState([]);
  const [draftTicket, setDraftTicket] = useState({});
  const [readyToFile, setReadyToFile] = useState(false);
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [input, setInput] = useState("");
  const [screenshotId, setScreenshotId] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [shipped, setShipped] = useState([]);
  const [submittedTicket, setSubmittedTicket] = useState(null);
  const scrollRef = useRef(null);

  // Auto-scroll chat to the latest message. #271: also re-fire when the
  // drawer (re)opens or the view flips back from tickets — the scroll
  // container remounts with scrollTop=0 in those cases and `messages`
  // alone never changes, so reopening used to land at the TOP of the
  // thread. rAF waits for the remounted container to lay out first.
  useEffect(() => {
    if (!open) return undefined;
    const raf = requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [messages, sending, open, view]);

  // Seed greeting + load shipped-recently feed once when first opened.
  useEffect(() => {
    if (!open || messages.length > 0) return;
    setMessages([
      {
        role: "mira",
        text: "Hi, I'm Mira — your support assistant. What can I help with?",
      },
    ]);
    miraShipped()
      .then((r) => setShipped(r?.data?.items || []))
      .catch(() => setShipped([]));
  }, [open, messages.length]);

  // Reset transient UI when the drawer is closed AFTER a successful submit.
  // Operator can intentionally close mid-conversation though — we keep
  // their session alive for 24h on the backend, so they can reopen and
  // pick up where they left off.
  const resetSession = useCallback(() => {
    setSessionId(null);
    setMessages([]);
    setQuickReplies([]);
    setDraftTicket({});
    setReadyToFile(false);
    setInput("");
    setScreenshotId(null);
    setScreenshotPreview(null);
    setSubmittedTicket(null);
  }, []);

  // #271 — header "New chat" button. Clears the thread + backend
  // session id; the greeting effect re-seeds automatically because
  // `messages` goes empty while the drawer is open.
  const startNewChat = useCallback(() => {
    resetSession();
    setView("chat");
  }, [resetSession]);

  const sendTurn = useCallback(
    async (text, categoryHint = null) => {
      const trimmed = (text || "").trim();
      if (!trimmed || sending) return;
      setSending(true);
      setQuickReplies([]);
      setMessages((m) => [...m, { role: "user", text: trimmed }]);
      try {
        const body = {
          message: trimmed,
          session_id: sessionId,
        };
        if (!sessionId) {
          // First turn → attach full telemetry + category hint.
          body.telemetry = collectTelemetry(user);
          if (categoryHint) body.category_hint = categoryHint;
        }
        const { data } = await miraChat(body);
        if (data.session_id && !sessionId) setSessionId(data.session_id);
        setMessages((m) => [...m, { role: "mira", text: data.reply }]);
        setQuickReplies(data.quick_replies || []);
        setDraftTicket(data.ticket || {});
        setReadyToFile(!!data.ready_to_file);
      } catch (e) {
        const detail =
          e?.response?.data?.detail ||
          e?.message ||
          "I had trouble reaching the assistant — try again?";
        setMessages((m) => [
          ...m,
          { role: "mira", text: `Sorry — ${detail}` },
        ]);
      } finally {
        setSending(false);
      }
    },
    [sessionId, sending, user]
  );

  const handleChipClick = (chip) => {
    // If chat already has substance, just paste the seed into the input.
    // Otherwise auto-fire it as the first turn so the user doesn't have
    // to type at all when the chip captures their intent.
    if (messages.length > 1) {
      setInput((prev) => (prev ? prev : chip.seed));
      return;
    }
    sendTurn(chip.seed || chip.label, chip.id);
  };

  const handleQuickReply = (text) => {
    // De-dup guard: if the same reply was just sent, ignore taps.
    if (sending) return;
    sendTurn(text);
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    sendTurn(text);
  };

  const handleCaptureScreen = async () => {
    if (capturing || !sessionId) {
      if (!sessionId) {
        toast.info("Tell me about it first — then I'll grab the screenshot.");
      }
      return;
    }
    setCapturing(true);
    try {
      // Lazy-load html2canvas the first time the user asks for a shot.
      const { default: html2canvas } = await import("html2canvas");
      // Hide the widget itself before capture so we don't shoot ourselves.
      const widgetEl = document.querySelector("[data-mira-widget]");
      if (widgetEl) widgetEl.style.visibility = "hidden";
      let canvas;
      try {
        canvas = await html2canvas(document.body, {
          backgroundColor: "#ffffff",
          scale: Math.min(window.devicePixelRatio || 1, 2),
          logging: false,
          useCORS: true,
          // Keep file size manageable
          windowWidth: Math.min(document.documentElement.clientWidth, 1920),
        });
      } finally {
        if (widgetEl) widgetEl.style.visibility = "visible";
      }
      const dataUrl = canvas.toDataURL("image/png");
      setScreenshotPreview(dataUrl);
      const { data } = await miraCaptureScreen({
        session_id: sessionId,
        image: dataUrl,
      });
      setScreenshotId(data.screenshot_id);
      toast.success("Screenshot attached", {
        description: `${Math.round((data.size || 0) / 1024)} KB`,
      });
    } catch (e) {
      toast.error("Couldn't capture the screen — try again.");
      setScreenshotPreview(null);
    } finally {
      setCapturing(false);
    }
  };

  const handleSubmit = async () => {
    if (submitting || !sessionId) return;
    setSubmitting(true);
    try {
      const { data } = await miraSubmit({
        session_id: sessionId,
        screenshot_id: screenshotId || undefined,
      });
      setSubmittedTicket(data);
      toast.success(`Filed — ticket #${data.issue_number}`, {
        description: data.already_filed ? "Already filed earlier" : "Thanks for the report!",
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "couldn't file";
      toast.error(`Couldn't file ticket — ${detail}`);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Bail when user has hidden the widget ────────────────────────
  if (hidden) return null;

  // ─── Pill (closed state) ─────────────────────────────────────────
  return (
    <div data-mira-widget data-testid="mira-widget-root">
      <AnimatePresence>
        {false && !open && (
          <motion.button
            key="pill"
            type="button"
            onClick={() => setOpen(true)}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            className="fixed bottom-5 right-5 z-[60] h-12 w-12 rounded-full bg-slate-900 p-1 shadow-lg shadow-slate-900/30 hover:bg-slate-800 transition-colors grid place-items-center group"
            aria-label="Open Mira support assistant"
            title="Mira — support assistant"
            data-testid="mira-pill"
          >
            {/* #271 — logo-only launcher (was logo + "Mira" text). The
                MARFI mark is recognisable on its own and the smaller
                footprint stops the pill covering page content. */}
            <span className="relative h-full w-full rounded-full bg-rose-600 grid place-items-center overflow-hidden ring-1 ring-rose-400/60">
              <img
                src="/marfi-logo.jpeg"
                alt=""
                className="h-full w-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
              <span className="absolute inset-0 ring-2 ring-rose-300/50 rounded-full pointer-events-none animate-pulse" />
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* ─── Drawer ──────────────────────────────────────────────── */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="drawer"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="fixed bottom-5 right-5 z-[60] w-[380px] max-w-[calc(100vw-24px)] h-[600px] max-h-[calc(100vh-48px)] bg-white rounded-2xl shadow-2xl shadow-slate-900/40 flex flex-col overflow-hidden border border-slate-200"
            data-testid="mira-drawer"
          >
            {/* Header — slate-900 with MARFI logo */}
            <div className="bg-slate-900 text-white px-4 py-3 flex items-center gap-3">
              <span className="relative h-9 w-9 rounded-full bg-rose-600 grid place-items-center overflow-hidden ring-1 ring-rose-400/60 shrink-0">
                <img
                  src="/marfi-logo.jpeg"
                  alt=""
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-semibold leading-tight" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="mira-title">
                  Mira
                </div>
                <div className="text-[11px] text-slate-300 flex items-center gap-1">
                  by MARFI
                  <ExternalLink className="w-3 h-3 opacity-70" />
                </div>
              </div>
              {/* ✺ My tickets pill */}
              <button
                type="button"
                onClick={() => setView(view === "tickets" ? "chat" : "tickets")}
                className="text-[11px] uppercase tracking-wide font-semibold text-slate-200 hover:text-white bg-slate-800 hover:bg-slate-700 px-2.5 py-1 rounded-full flex items-center gap-1.5 transition-colors"
                data-testid="mira-my-tickets-toggle"
              >
                {view === "tickets" ? (
                  <>
                    <ChevronLeft className="w-3 h-3" />
                    <span>Back</span>
                  </>
                ) : (
                  <span>My tickets</span>
                )}
              </button>
              {/* #271 — New chat: clear the thread and start fresh.
                  Disabled mid-send so an in-flight reply can't land in
                  a freshly cleared conversation. */}
              <button
                type="button"
                onClick={startNewChat}
                disabled={sending || submitting}
                className="text-slate-300 hover:text-white p-1 rounded-md hover:bg-slate-800 disabled:opacity-40"
                aria-label="Start a New Chat"
                title="New chat"
                data-testid="mira-new-chat"
              >
                <SquarePen className="w-4 h-4" />
              </button>
              {/* Hide-widget shortcut — MIRA ticket #267. Persists in
                  localStorage; re-enable from the /profile page. */}
              <button
                type="button"
                onClick={hideWidget}
                className="text-slate-300 hover:text-white p-1 rounded-md hover:bg-slate-800"
                aria-label="Hide MIRA from this page"
                title="Hide Mira (re-enable from your Profile page)"
                data-testid="mira-hide"
              >
                <EyeOff className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-slate-300 hover:text-white p-1 rounded-md hover:bg-slate-800"
                aria-label="Close MIRA"
                data-testid="mira-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body — switches between chat and tickets */}
            {view === "tickets" ? (
              <MyTicketsPanel onClose={() => setView("chat")} />
            ) : (
              <>
                {/* Standing-by status bar */}
                <div className="px-4 py-1.5 bg-slate-50 border-b border-slate-200 flex items-center gap-2 text-[10px] text-slate-500">
                  <span className="relative h-1.5 w-1.5 rounded-full bg-emerald-500">
                    <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping" />
                  </span>
                  <span>{sending ? "Thinking…" : "Standing by"}</span>
                  <span className="ml-auto font-mono uppercase tracking-wide text-slate-400">
                    Gemini 3 Flash
                  </span>
                </div>

                {/* Chat scroll area */}
                <div
                  ref={scrollRef}
                  className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-white"
                  data-testid="mira-chat-scroll"
                >
                  {messages.map((m, i) => (
                    <div
                      key={i}
                      className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} items-end gap-2`}
                    >
                      {m.role === "mira" && (
                        <span className="h-6 w-6 rounded-full bg-rose-600 overflow-hidden shrink-0 ring-1 ring-rose-400/60">
                          <img src="/marfi-logo.jpeg" alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                        </span>
                      )}
                      <div
                        className={`text-[13.5px] leading-snug px-3 py-2 rounded-2xl max-w-[78%] whitespace-pre-wrap break-words ${
                          m.role === "user"
                            ? "bg-slate-900 text-white rounded-br-sm"
                            : "bg-slate-100 text-slate-900 rounded-bl-sm"
                        }`}
                        data-testid={`mira-msg-${m.role}-${i}`}
                      >
                        {m.text}
                      </div>
                    </div>
                  ))}

                  {/* Category chips — only show on first turn */}
                  {messages.length === 1 && !sending && (
                    <div className="space-y-2 pt-1" data-testid="mira-category-chips">
                      {CATEGORY_CHIPS.map((chip) => (
                        <button
                          key={chip.id}
                          type="button"
                          onClick={() => handleChipClick(chip)}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl border ${chip.border} ${chip.bg} ${chip.color} text-[13px] font-medium text-left transition-colors`}
                          data-testid={`mira-chip-${chip.id}`}
                        >
                          <chip.Icon className="w-4 h-4 shrink-0" />
                          <span className="truncate">{chip.label}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Quick replies */}
                  {quickReplies.length > 0 && !sending && (
                    <div className="flex flex-wrap gap-1.5 pt-1" data-testid="mira-quick-replies">
                      {quickReplies.map((q, i) => (
                        <button
                          key={`${q}-${i}`}
                          type="button"
                          onClick={() => handleQuickReply(q)}
                          className="text-[12px] px-2.5 py-1 rounded-full bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition-colors"
                          data-testid={`mira-quick-${i}`}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  )}

                  {sending && (
                    <div className="flex items-center gap-2 text-[12px] text-slate-500 pl-8" data-testid="mira-thinking">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Mira is thinking…</span>
                    </div>
                  )}

                  {/* Submitted confirmation */}
                  {submittedTicket && (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-[12.5px] text-emerald-900 space-y-1" data-testid="mira-filed-confirmation">
                      <div className="font-semibold flex items-center gap-1.5">
                        <span>Thanks — your ticket is filed.</span>
                      </div>
                      <div className="text-emerald-700">
                        Our team will follow up. You can track its status under <span className="font-semibold">My tickets</span>.
                      </div>
                      <div className="flex gap-2 pt-1">
                        <button
                          type="button"
                          onClick={() => setView("tickets")}
                          className="text-[11px] underline text-emerald-700 hover:text-emerald-900"
                          data-testid="mira-filed-view-tickets"
                        >
                          View my tickets
                        </button>
                        <button
                          type="button"
                          onClick={resetSession}
                          className="text-[11px] text-slate-600 hover:text-slate-900 underline ml-auto"
                          data-testid="mira-file-another"
                        >
                          File another
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Screenshot preview strip */}
                {screenshotPreview && (
                  <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 flex items-center gap-2" data-testid="mira-screenshot-preview">
                    <ImageIcon className="w-3.5 h-3.5 text-slate-500" />
                    <img
                      src={screenshotPreview}
                      alt="screenshot preview"
                      className="h-8 w-10 object-cover rounded border border-slate-300"
                    />
                    <span className="text-[11px] text-slate-600">Attached</span>
                    <button
                      type="button"
                      onClick={() => {
                        setScreenshotPreview(null);
                        setScreenshotId(null);
                      }}
                      className="ml-auto text-[11px] text-slate-500 hover:text-rose-600"
                      data-testid="mira-screenshot-remove"
                    >
                      Remove
                    </button>
                  </div>
                )}

                {/* "Ready to file" CTA strip */}
                {readyToFile && !submittedTicket && (
                  <div className="px-4 py-2 border-t border-slate-100 bg-emerald-50 flex items-center gap-2" data-testid="mira-ready-strip">
                    <span className="text-[12px] text-emerald-900 flex-1">
                      Ready to file:{" "}
                      <span className="font-semibold">
                        {draftTicket?.title || "your ticket"}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={submitting}
                      className="text-[12px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 px-3 py-1 rounded-full"
                      data-testid="mira-submit"
                    >
                      {submitting ? "Filing…" : "File ticket"}
                    </button>
                  </div>
                )}

                {/* Input bar */}
                <div className="border-t border-slate-200 px-3 py-2.5 flex items-center gap-2 bg-white">
                  <button
                    type="button"
                    onClick={handleCaptureScreen}
                    disabled={capturing || !sessionId}
                    title={
                      !sessionId
                        ? "Send your first message, then attach a screenshot"
                        : "Capture this screen"
                    }
                    className="text-slate-500 hover:text-slate-900 disabled:opacity-40 p-1.5 rounded-md hover:bg-slate-100"
                    data-testid="mira-capture-screen"
                  >
                    {capturing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                  </button>
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    placeholder="Type a message or tap an option…"
                    disabled={sending}
                    className="flex-1 text-[13px] px-3 py-1.5 rounded-full border border-slate-300 focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200 bg-white"
                    data-testid="mira-input"
                  />
                  <button
                    type="button"
                    onClick={handleSend}
                    disabled={!input.trim() || sending}
                    className="bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-white p-1.5 rounded-full"
                    aria-label="Send"
                    data-testid="mira-send"
                  >
                    <Send className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Footer — "Shipped recently" + brand */}
                <div className="bg-slate-50 border-t border-slate-200 px-4 py-2 text-[10.5px] text-slate-500 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {shipped.length > 0 ? (
                      <>
                        <span className="font-semibold text-emerald-700">Shipped:</span>
                        <span
                          className="truncate text-slate-600"
                          data-testid="mira-shipped-link"
                          title={shipped[0].title}
                        >
                          {shipped[0].title}
                        </span>
                      </>
                    ) : (
                      <span>
                        Mira by{" "}
                        <span className="text-slate-600">MARFI</span>
                      </span>
                    )}
                  </div>
                  <span className="text-slate-400 shrink-0">Powered by Gemini 3 Flash</span>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
